// Adding a contact (docs/phase-3-messages.md § Ajouter un contact).
//
// The host shows `#/invite/<secret>`; the guest opens it. Both meet in the room of that one-time
// secret (DTLS-bound proof, like linking), then each sends a hello: name, identity key, and its
// attested devices. Each side checks every attestation and computes the safety number. The host
// confirms; it creates the conversation and seals its secrets for the guest's device.

import { Mesh } from '../core/mesh';
import { deriveAuthKey } from '../core/auth';
import { randomSecret, roomIdFor } from '../core/ids';
import { openSealed, sealFor, type DeviceRecord, type SpaceSecrets } from '../space/keys';
import { safeLabel } from '../drop/protocol';
import { checkAttestation, safetyNumber } from './identity';
import type { Conversations } from './conversations';
import type { Person } from './conversation';

const INVITE_TTL_MS = 10 * 60_000;
const MAX_DEVICES = 16;

export type Hello = { person: string; name: string; self: string; devices: DeviceRecord[] };
export type InviteRequest = { peer: string; hello: Hello; safety: string };

const B64KEY = /^[A-Za-z0-9_-]{43}$/;

function parseDevice(raw: unknown): DeviceRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !/^[0-9a-f]{32}$/.test(r.id)) return null;
  for (const k of ['signPub', 'dhPub', 'person']) if (typeof r[k] !== 'string' || !B64KEY.test(r[k] as string)) return null;
  if (typeof r.personSig !== 'string' || !/^[A-Za-z0-9_-]{86}$/.test(r.personSig)) return null;
  return { id: r.id, name: safeLabel(r.name), signPub: r.signPub as string, dhPub: r.dhPub as string, person: r.person as string, personSig: r.personSig };
}

/** Validates a hello and every attestation in it; null if anything is off. */
export async function parseHello(raw: unknown): Promise<Hello | null> {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.person !== 'string' || !B64KEY.test(m.person) || typeof m.self !== 'string' || !Array.isArray(m.devices) || m.devices.length > MAX_DEVICES) return null;
  const devices = m.devices.map(parseDevice);
  if (devices.some((d) => !d || d.person !== m.person)) return null;
  for (const d of devices) if (!(await checkAttestation(d!))) return null;
  if (!devices.some((d) => d!.id === m.self)) return null;
  return { person: m.person, name: safeLabel(m.name), self: m.self, devices: devices as DeviceRecord[] };
}

async function inviteMesh(secret: string, wsUrl: (p: string) => string, iceServers: RTCIceServer[]): Promise<Mesh> {
  return new Mesh({ url: wsUrl(`/ws/room/${await roomIdFor(`invite:${secret}`)}`), iceServers, authKey: await deriveAuthKey(`invite-v1:${secret}`) });
}

async function myHello(convs: Conversations): Promise<Hello> {
  return { person: convs.identity.pub, name: convs.me.name, self: convs.space.device.id, devices: await convs.myDevices() };
}

/** Events: 'request' {peer, hello, safety}, 'done' {conv}, 'expired'. */
export class InviteHost extends EventTarget {
  readonly secret = randomSecret();
  request: InviteRequest | null = null;
  #mesh: Mesh | null = null;
  #timer: ReturnType<typeof setTimeout> | undefined;

  constructor(readonly convs: Conversations) {
    super();
  }

  link(origin: string): string {
    return `${origin}/#/invite/${this.secret}`;
  }

  async start(wsUrl: (p: string) => string, iceServers: RTCIceServer[]): Promise<void> {
    const mesh = await inviteMesh(this.secret, wsUrl, iceServers);
    this.#mesh = mesh;
    const hello = await myHello(this.convs);
    mesh.addEventListener('authed', (e) => mesh.send((e as CustomEvent).detail.peer, { t: 'hello', ...hello }));
    mesh.addEventListener('ctl', async (e) => {
      const { peer, msg } = (e as CustomEvent).detail as { peer: string; msg: { t?: string } };
      if (msg?.t !== 'hello' || this.request) return;
      const theirs = await parseHello(msg);
      if (!theirs || theirs.person === this.convs.identity.pub) return;
      this.request = { peer, hello: theirs, safety: await safetyNumber(this.convs.identity.pub, theirs.person) };
      this.dispatchEvent(new CustomEvent('request', { detail: this.request }));
    });
    await mesh.start();
    this.#timer = setTimeout(() => {
      this.stop();
      this.dispatchEvent(new Event('expired'));
    }, INVITE_TTL_MS);
  }

  async accept(): Promise<string> {
    const req = this.request!;
    const person: Person = { identity: req.hello.person, name: req.hello.name };
    const secrets = await this.convs.createAsHost(person, req.hello.devices);
    const guest = req.hello.devices.find((d) => d.id === req.hello.self)!;
    const sealed = await sealFor(this.convs.space.device, guest.dhPub, JSON.stringify(secrets));
    this.#mesh?.send(req.peer, { t: 'accept', sealed, host: this.convs.space.device.dhPub });
    this.dispatchEvent(new CustomEvent('done', { detail: { conv: secrets.spaceId } }));
    setTimeout(() => this.stop(), 3000);
    return secrets.spaceId;
  }

  decline(): void {
    if (this.request) this.#mesh?.send(this.request.peer, { t: 'declined' });
    this.request = null;
  }

  stop(): void {
    clearTimeout(this.#timer);
    this.#mesh?.close();
    this.#mesh = null;
  }
}

/**
 * Guest side. `onHello` receives the host's verified hello and the safety number (to display);
 * resolves with the conversation id once the host accepted, rejects if it declined.
 */
export async function acceptInvite(
  secret: string,
  convs: Conversations,
  wsUrl: (p: string) => string,
  iceServers: RTCIceServer[],
  onHello: (host: Hello, safety: string) => void,
): Promise<{ conv: string; close: () => void }> {
  const mesh = await inviteMesh(secret, wsUrl, iceServers);
  const hello = await myHello(convs);
  let host: Hello | null = null;
  return new Promise((resolve, reject) => {
    mesh.addEventListener('authed', (e) => mesh.send((e as CustomEvent).detail.peer, { t: 'hello', ...hello }));
    mesh.addEventListener('ctl', async (e) => {
      const { msg } = (e as CustomEvent).detail as { msg: { t?: string; sealed?: { from: string; nonce: string; ct: string }; host?: string } };
      if (msg?.t === 'hello' && !host) {
        host = await parseHello(msg);
        if (host && host.person !== convs.identity.pub) onHello(host, await safetyNumber(convs.identity.pub, host.person));
        return;
      }
      if (msg?.t === 'declined') {
        mesh.close();
        return reject(new Error('declined'));
      }
      if (msg?.t !== 'accept' || !host || !msg.sealed) return;
      const hostDevice = host.devices.find((d) => d.id === host!.self)!;
      try {
        const secrets = JSON.parse(await openSealed(convs.space.device, hostDevice.dhPub, msg.sealed)) as SpaceSecrets;
        if (typeof secrets.spaceId !== 'string' || !secrets.secrets?.[secrets.epoch]) throw new Error('bad secrets');
        await convs.joinAsGuest(secrets, { identity: host.person, name: host.name });
        resolve({ conv: secrets.spaceId, close: () => mesh.close() });
      } catch (err) {
        mesh.close();
        reject(err);
      }
    });
    mesh.start().catch(reject);
  });
}
